/**
 * CustomerSegmentationPage — `/customer-service/segments`.
 *
 * Two live segmentation views combined:
 *   - GET /v1/customers/lookup    — group customers by `Customer.grade`
 *   - GET /v1/service-desk/segments — RFM bucket per customer derived from
 *     case activity (Recency = lastCaseAt, Frequency = caseCount; the
 *     M-axis is omitted because service-desk has no monetary signal).
 *
 * The previous "RFM segmentation deferred" notice is gone — buckets now
 * render live counts.
 */
import type { JSX } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Users } from 'lucide-react';

import { ApiError } from '../../lib/api/client';
import { lookupCustomers } from '../../lib/api/customers';
import { listCustomerSegments } from '../../lib/api/service-desk';
import { DeferredNotice, PageHeader } from '../shared/StaffUI';
import { KPICard } from '../mail/shared/MailUI';

export function CustomerSegmentationPage(): JSX.Element {
  const customers = useQuery({
    queryKey: ['cs', 'customers', 'segments'],
    queryFn: () => lookupCustomers({ pageSize: 100 }),
    retry: false,
    staleTime: 30_000,
  });
  const segments = useQuery({
    queryKey: ['cs', 'segments', 'rfm'],
    queryFn: () => listCustomerSegments({ windowDays: 90, limit: 200 }),
    retry: false,
    staleTime: 30_000,
  });

  const denied =
    customers.isError && customers.error instanceof ApiError && (customers.error.status === 401 || customers.error.status === 403);
  const segmentsDenied =
    segments.isError && segments.error instanceof ApiError && (segments.error.status === 401 || segments.error.status === 403);

  // Group by `grade` — these are row counts, not money.
  const counts: Record<string, number> = {};
  for (const c of customers.data?.items ?? []) {
    counts[c.grade] = (counts[c.grade] ?? 0) + 1;
  }

  return (
    <div className="space-y-6">
      <PageHeader title="Customer Segments" description="Grade groups and RFM buckets derived from case activity." />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard
          title="Sample size"
          value={customers.isPending ? '—' : denied ? '—' : String(customers.data?.total ?? 0)}
          hint={denied ? 'Permission required' : 'Total customers in this segment cut'}
          icon={Users}
          tone={denied ? 'warning' : 'default'}
        />
      </div>

      <section className="space-y-3">
        <h2 className="text-sm font-semibold text-gray-900">Grade segmentation</h2>
        {customers.isPending ? (
          <p className="text-sm text-muted">Loading…</p>
        ) : denied ? (
          <DeferredNotice title="Permission required">
            The dev token doesn&apos;t carry <code className="font-mono">Customer/auditRead</code>.
          </DeferredNotice>
        ) : customers.isError ? (
          <DeferredNotice title="Service unreachable">{(customers.error as Error).message}</DeferredNotice>
        ) : (
          <div className="bg-white border border-border rounded-xl overflow-hidden">
            <table data-testid="grade-table" className="w-full text-sm">
              <thead className="bg-gray-50 text-[10px] uppercase tracking-wider text-muted">
                <tr>
                  <th className="text-left px-4 py-2">Segment (grade)</th>
                  <th className="text-left px-4 py-2">Customers</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {Object.entries(counts).map(([grade, count]) => (
                  <tr key={grade} className="hover:bg-gray-50">
                    <td className="px-4 py-2 text-xs">{grade}</td>
                    <td className="px-4 py-2 text-xs">{count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {Object.keys(counts).length === 0 ? <p className="p-8 text-center text-sm text-muted">No customers yet.</p> : null}
          </div>
        )}
      </section>

      <section className="space-y-3">
        <header className="flex items-baseline justify-between">
          <h2 className="text-sm font-semibold text-gray-900">RFM segmentation</h2>
          <span className="text-xs text-muted">
            window: {segments.data?.windowDays ?? 90}d · {segments.data?.totalCustomers ?? 0} customers
          </span>
        </header>
        {segments.isPending ? (
          <p className="text-sm text-muted">Loading…</p>
        ) : segmentsDenied ? (
          <DeferredNotice title="Permission required">
            The dev token doesn&apos;t carry <code className="font-mono">ServiceDesk/readSegments</code>.
          </DeferredNotice>
        ) : segments.isError ? (
          <DeferredNotice title="Service unreachable">{(segments.error as Error).message}</DeferredNotice>
        ) : (
          <div className="bg-white border border-border rounded-xl overflow-hidden">
            <table data-testid="rfm-table" className="w-full text-sm">
              <thead className="bg-gray-50 text-[10px] uppercase tracking-wider text-muted">
                <tr>
                  <th className="text-left px-4 py-2">Segment</th>
                  <th className="text-left px-4 py-2">Customers</th>
                  <th className="text-left px-4 py-2">Definition</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {(segments.data?.buckets ?? []).map((b) => (
                  <tr key={b.segment} data-testid={`rfm-bucket-${b.segment}`} className="hover:bg-gray-50">
                    <td className="px-4 py-2 text-xs font-semibold">{b.segment}</td>
                    <td className="px-4 py-2 text-xs">{b.customerCount}</td>
                    <td className="px-4 py-2 text-xs text-muted">{b.description}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {(segments.data?.totalCustomers ?? 0) === 0 ? (
              <p className="p-8 text-center text-sm text-muted">No customer activity in the window yet.</p>
            ) : null}
          </div>
        )}
      </section>
    </div>
  );
}
